import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

/**
 * Generic loader/cache for a KayKit-style character pack
 * (characters/animations ship as self-contained .glb, equipment ships as
 * flat .gltf+.bin referencing a shared texture atlas by relative URI).
 * Extracted from adventurers-cache.ts once a second character pack
 * (frontend/public/assets/skeletons/) needed the identical loader — that
 * file's own comment flagged this as the "extract on 3rd occurrence"
 * trigger point (dungeon-pack's gltf-cache.ts was the 2nd, differently
 * shaped one). `adventurers-cache.ts` now delegates here; its public API
 * is unchanged.
 */

interface LoadedGltf {
  scene: THREE.Group;
  animations: THREE.AnimationClip[];
}

/**
 * Which shared animation files a pack's rigs need, beyond `core`.
 *
 * Keyed by rig because the sets are not uniform: the skeleton monsters want
 * the `undead` clips on the medium rig (minion, warrior), while the same
 * pack's `golem` is a large-rig body with no undead set of its own.
 */
export type ExtraAnimationSets = Partial<Record<string, readonly string[]>>;

export interface CharacterPackCache {
  loadCharacterModel(modelName: string): Promise<THREE.Group>;
  loadEquipmentModel(modelName: string): Promise<THREE.Group>;
  /**
   * A generated worn-gear piece (`tools/make-armour.py`).
   *
   * Its own directory rather than `equipment/` because the two are not the
   * same kind of object: an equipment model is handed to a bone by name and
   * carries no idea where it belongs, while a gear piece is authored *in
   * rest-pose model space* and only makes sense against this rig. Keeping the
   * paths apart keeps that difference visible at the call site.
   */
  loadGearModel(defId: string): Promise<THREE.Group>;
  loadRigAnimations(rig: string): Promise<THREE.AnimationClip[]>;
  /** Premium emote clips for `rig`, fetched only when one is actually needed. */
  loadEmoteClips(rig: string): Promise<THREE.AnimationClip[]>;
  /** design/gdd/cosmetics-store.md's render hook — a recolored variant of a character's base texture atlas, same UV layout, swapped in by character-visual.ts. */
  loadSkinTexture(skinAssetKey: string): Promise<THREE.Texture>;
  /**
   * An additive-blend variant of one of the rig's own clips (see
   * `THREE.AnimationUtils.makeClipAdditive`), for layering a pose on top of
   * whatever base animation is already playing. Built once per
   * rig+source+reference triple and cached here, never on the original clip
   * returned by `loadRigAnimations` — that object is shared by every character
   * instance of the rig as a full-replace clip, and `makeClipAdditive` mutates
   * its keyframes in place.
   *
   * `referenceClipName` is the pose the delta is measured *from*, and choosing
   * it is the difference between a visible overlay and an invisible one:
   *
   * - omitted (the hit flinch, Game Feel Polish Phase 2c) uses the clip's own
   *   first frame, which for `Hit_A` is the neutral pre-impact pose — so the
   *   delta is the flinch.
   * - given (the block guard) points at `Idle_A`, because a guard *loop*'s own
   *   first frame is already the guard stance, and a delta from that would
   *   carry only the idle sway inside the loop, not the stance itself.
   */
  loadAdditiveClip(rig: string, sourceClipName: string, referenceClipName?: string): Promise<THREE.AnimationClip>;
}

/**
 * Where the shared clip libraries live — **outside** any one pack.
 *
 * Animations used to sit under each pack (`adventurers/animations/`,
 * `skeletons/animations/`) and the two folders held byte-identical files: both
 * packs are built on the same KayKit rigs, so both shipped the same 5.4MB.
 * They are imported once now, by `tools/import-animations.py`, and both packs
 * point here.
 */
const ANIMATIONS_PATH = '/assets/animations';

export function createCharacterPackCache(
  basePath: string,
  extraSets: ExtraAnimationSets = {},
): CharacterPackCache {
  const loader = new GLTFLoader();
  const textureLoader = new THREE.TextureLoader();
  const characterCache = new Map<string, Promise<THREE.Group>>();
  const equipmentCache = new Map<string, Promise<THREE.Group>>();
  const gearCache = new Map<string, Promise<THREE.Group>>();
  const animationCache = new Map<string, Promise<THREE.AnimationClip[]>>();
  const skinCache = new Map<string, Promise<THREE.Texture>>();
  const additiveClipCache = new Map<string, Promise<THREE.AnimationClip>>();

  function loadGltf(url: string): Promise<LoadedGltf> {
    return new Promise((resolve, reject) => {
      loader.load(
        url,
        (gltf) => resolve({ scene: gltf.scene, animations: gltf.animations }),
        undefined,
        (err) => reject(new Error(`Failed to load "${url}": ${String(err)}`)),
      );
    });
  }

  /**
   * Every clip this pack plays for `rig`: the shared `core` set, plus whatever
   * extra sets the pack declared for that rig.
   *
   * One file per set rather than one per KayKit clip family (General,
   * MovementBasic, CombatMelee, MovementAdvanced, three clips of CombatRanged)
   * because the import merges them — see `tools/import-animations.py`. That
   * turned five requests into one and, with the mannequin preview mesh
   * stripped, more than doubled the clip count for fewer bytes than the two
   * files this replaced.
   */
  function loadRigAnimationsInternal(rig: string): Promise<THREE.AnimationClip[]> {
    let pending = animationCache.get(rig);
    if (!pending) {
      const sets = ['core', ...(extraSets[rig] ?? [])];
      pending = Promise.all(sets.map((set) => loadGltf(`${ANIMATIONS_PATH}/rig_${rig}_${set}.glb`)))
        .then((loaded) => loaded.flatMap((g) => g.animations));
      animationCache.set(rig, pending);
    }
    return pending;
  }

  /**
   * The premium emote clips, loaded on demand and never as part of `core`.
   *
   * Separate from `loadRigAnimations` on purpose: emotes are Ember-only
   * cosmetics (`design/gdd/cosmetics-store.md`), so most players own none, and
   * putting 215KB of dances in the set every player downloads to enter a
   * dungeon would charge everyone for content only some can use.
   */
  function loadEmoteClipsInternal(rig: string): Promise<THREE.AnimationClip[]> {
    const key = `emotes:${rig}`;
    let pending = animationCache.get(key);
    if (!pending) {
      pending = loadGltf(`${ANIMATIONS_PATH}/rig_${rig}_emotes.glb`).then((g) => g.animations);
      animationCache.set(key, pending);
    }
    return pending;
  }

  return {
    loadCharacterModel(modelName: string): Promise<THREE.Group> {
      let pending = characterCache.get(modelName);
      if (!pending) {
        pending = loadGltf(`${basePath}/characters/${modelName}.glb`).then((r) => r.scene);
        characterCache.set(modelName, pending);
      }
      return pending;
    },

    loadEquipmentModel(modelName: string): Promise<THREE.Group> {
      let pending = equipmentCache.get(modelName);
      if (!pending) {
        pending = loadGltf(`${basePath}/equipment/${modelName}.gltf`).then((r) => r.scene);
        equipmentCache.set(modelName, pending);
      }
      return pending;
    },

    loadGearModel(defId: string): Promise<THREE.Group> {
      let pending = gearCache.get(defId);
      if (!pending) {
        pending = loadGltf(`${basePath}/gear/${defId}.gltf`).then((r) => r.scene);
        gearCache.set(defId, pending);
      }
      return pending;
    },

    loadRigAnimations(rig: string): Promise<THREE.AnimationClip[]> {
      return loadRigAnimationsInternal(rig);
    },

    loadEmoteClips(rig: string): Promise<THREE.AnimationClip[]> {
      return loadEmoteClipsInternal(rig);
    },

    loadSkinTexture(skinAssetKey: string): Promise<THREE.Texture> {
      let pending = skinCache.get(skinAssetKey);
      if (!pending) {
        pending = new Promise((resolve, reject) => {
          textureLoader.load(
            `${basePath}/skins/${skinAssetKey}.png`,
            (texture) => {
              // glTF UV space is not Y-flipped like a plain THREE.TextureLoader
              // assumes — matches the colorSpace/flipY GLTFLoader itself sets on
              // the character's own embedded base-color texture, so the swapped-in
              // texture lines up with the same UVs instead of rendering upside down.
              texture.flipY = false;
              texture.colorSpace = THREE.SRGBColorSpace;
              texture.needsUpdate = true;
              resolve(texture);
            },
            undefined,
            (err) => reject(new Error(`Failed to load skin texture "${skinAssetKey}": ${String(err)}`)),
          );
        });
        skinCache.set(skinAssetKey, pending);
      }
      return pending;
    },

    loadAdditiveClip(rig: string, sourceClipName: string, referenceClipName?: string): Promise<THREE.AnimationClip> {
      const key = `${rig}:${sourceClipName}:${referenceClipName ?? ''}`;
      let pending = additiveClipCache.get(key);
      if (!pending) {
        pending = loadRigAnimationsInternal(rig).then((clips) => {
          const source = clips.find((c) => c.name === sourceClipName);
          if (!source) throw new Error(`Additive source clip "${sourceClipName}" not found for rig "${rig}"`);
          if (!referenceClipName) return THREE.AnimationUtils.makeClipAdditive(source.clone());
          const reference = clips.find((c) => c.name === referenceClipName);
          if (!reference) throw new Error(`Additive reference clip "${referenceClipName}" not found for rig "${rig}"`);
          return THREE.AnimationUtils.makeClipAdditive(source.clone(), 0, reference);
        });
        additiveClipCache.set(key, pending);
      }
      return pending;
    },
  };
}
